import { Button, Col, Collapse, Input, Popover, Row, Space, Table } from "antd"
import { ColumnsType } from "antd/lib/table"
import moment from 'moment'
import { ReactNode, useMemo } from "react"
import Icon from "../../../../assets/icons/icon"
import CopyableContent from "../../../../components/copyableContent"
import CustomCollapse from "../../../../components/customCollapse"
import CustomCollapseHeader from "../../../../components/customCollapseHeader"
import InputNum from "../../../../components/form/inputNum"
import LockSwitch from "../../../../components/form/lockSwitch"
import { renderErrorMessage } from "../../../../components/messages/errorMessage"
import openModal from "../../../../components/modal/openModal"
import TaxCaseStore from "../../../../contexts/taxCaseStore"
import TaxFlowStore from "../../../../contexts/taxFlowStore"
import _t from "../../../../lang/translate"
import format from "../../../../utilities/formatNumbers"
import { safeNum } from "../../../../utilities/number"
import { isValidNum } from "../../../../utilities/typeGuard"
import { AdvertRegulation } from "../advert/types"
import TaxFlowAdverts from "./taxFlowAdverts"

export default function TaxFlowTradePrice() {

  return (
    <div style={{ padding: 16 }}>
      <CustomCollapse defaultActiveKey={['adverts']} className="ml-0 mr-0">
        <Collapse.Panel key={"adverts"} header={
          <CustomCollapseHeader>{_t("adverts")}</CustomCollapseHeader>
        }>
          <TaxFlowAdverts />
        </Collapse.Panel>
      </CustomCollapse>

      <CustomCollapse defaultActiveKey={['tradePriceCalculator']} className="ml-0 mr-0">
        <Collapse.Panel key={"tradePriceCalculator"} header={
          <CustomCollapseHeader>{_t("trade_price_calculator")}</CustomCollapseHeader>
        }>
          <TradePriceAdvertRegulations />
          <TradePriceCalculatorOutput />
        </Collapse.Panel>
      </CustomCollapse>

    </div>
  )
}

type AdvertRegulationsViewModel = {
  id: string;
  make: string | null;
  model: string | null;
  firstRegDate: string | null;
  ageInMonths: string;
  mileage: string;
  price: string | null;
  horsePower: string | null;
  equipmentPrice: string;
  isAveragesRow?: boolean;
  isEvalVehicleRow?: boolean;
  isDeviationRow?: boolean;
  isAdvertRow?: boolean;
  regulation?: AdvertRegulation | null;
}
function TradePriceAdvertRegulations() {
  const { includedAdverts, equipmentSums } = TaxFlowStore.useContainer()
  const { taxCase } = TaxCaseStore.useContainer()
  const equipmnetNewPricesMap = useMemo(() => {
    return Object.fromEntries(includedAdverts.map((ad) => {
      const sum = ad.config?.newPriceManager?.values.equipmentPrice
      return [ad.id, sum]
    }))
  }, [includedAdverts])

  const dataSource: AdvertRegulationsViewModel[] = useMemo(() => {
    const dataSource: AdvertRegulationsViewModel[] = []
    for (const ad of includedAdverts) {
      const equipmentPrice = equipmentSums[ad.id]
      dataSource.push({
        id: ad.id.toString(),
        make: ad.manufacturer ?? null,
        model: ad.name ?? null,
        firstRegDate: format.date(ad.firstRegDate),
        ageInMonths: moment().diff(ad.firstRegDate, 'month').toString(),
        mileage: int(ad.config?.regulations?.output?.ageRegulatedMileage),
        horsePower: ad.horsePower?.toString() ?? null,
        price: int(ad.config?.regulations?.output?.tradePriceLevel), // This should be the regulated amount
        equipmentPrice: format.number(equipmentPrice), // This should be the regulated amount.
        regulation: ad.config?.regulations ?? null,
        isAdvertRow: true,
      })
    }

    const average = includedAdverts.reduce((prev, curr, index, arr) => {
      const ageInMonths = moment().diff(curr.firstRegDate, 'month')
      const price = curr.config?.regulations?.output?.tradePriceLevel ?? 0
      const equipmentPrice = equipmentSums[curr.id]
      const currRegulatedMileage = curr.config?.regulations?.output?.ageRegulatedMileage ?? 0
      return {
        ageInMonths: (prev.ageInMonths ?? 0) + ((ageInMonths ?? 0) / arr.length),
        mileage: (prev.mileage ?? 0) + ((currRegulatedMileage ?? 0) / arr.length),
        price: (prev.price ?? 0) + (price / arr.length),
        equipmentPrice: (prev.equipmentPrice ?? 0) + (equipmentPrice / arr.length),
      }
    }, {
      ageInMonths: 0,
      mileage: 0,
      price: 0,
      equipmentPrice: 0,
    })
    dataSource.push({
      id: "Gennemsnit af ovenstående",
      make: null,
      model: null,
      firstRegDate: null,
      ageInMonths: int(average.ageInMonths),
      mileage: int(average.mileage),
      horsePower: null,
      price: int(average.price),
      equipmentPrice: int(average.equipmentPrice),
      isAveragesRow: true
    })

    const taxVehicleEquipmentPrice = equipmentSums.evaluationVehicle
    dataSource.push({
      id: "Køretøj til beregning",
      make: null,
      model: null,
      firstRegDate: format.date(taxCase.vehicle?.firstRegDate),
      ageInMonths: moment().diff(taxCase.vehicle?.firstRegDate, 'month').toString(),
      mileage: int(taxCase.vehicle?.mileage),
      horsePower: taxCase.vehicle?.horsePower ?? null,
      price: null,
      equipmentPrice: int(taxVehicleEquipmentPrice),
      isEvalVehicleRow: true
    })

    const mileageDiff = (taxCase.vehicle?.mileage ?? 0) - average.mileage;
    const mileageDeviation = average.mileage !== 0
      ? mileageDiff / average.mileage
      : 0;

    const deviation = {
      age: -0.02,
      mileageDiff,
      mileageDeviation,
      equipmentDiff: (taxVehicleEquipmentPrice ?? 0) - average.equipmentPrice,
    }

    dataSource.push({
      id: "Afvigelse",
      make: null,
      model: null,
      firstRegDate: null,
      ageInMonths: format.percent(deviation.age),
      mileage: `${deviation.mileageDiff > 0 ? "+" : ""}${int(deviation.mileageDiff)}/${format.percent(deviation.mileageDeviation, { maximumFractionDigits: 0 })}`,
      horsePower: null,
      price: null,
      equipmentPrice: int(deviation.equipmentDiff),
      isDeviationRow: true
    })

    return dataSource
  }, [taxCase.vehicle, includedAdverts, equipmentSums]);

  const columns: ColumnsType<AdvertRegulationsViewModel> = [
    {
      key: "id",
      dataIndex: "id",
      title: "ID",
      width: 64,
      render(value, record) {
        return {
          children: value,
          props: {
            colSpan: (record.isAveragesRow || record.isEvalVehicleRow || record.isDeviationRow) ? 3 : 1,
          }
        }
      },
    },
    {
      key: "make",
      dataIndex: "make",
      title: _t("brand"),
      width: 124,
      render(value, record, index) {
        return {
          children: value,
          props: {
            colSpan: (record.isAveragesRow || record.isEvalVehicleRow || record.isDeviationRow) ? 0 : 1,
          }
        }
      },
    },
    {
      key: "model",
      dataIndex: "model",
      title: _t("model"),
      render(value, record, index) {
        return {
          children: value,
          props: {
            colSpan: (record.isAveragesRow || record.isEvalVehicleRow || record.isDeviationRow) ? 0 : 1,
          }
        }
      },
    },
    {
      key: "firstRegDate",
      dataIndex: "firstRegDate",
      title: _t("firstRegDate"),
      width: 96,
    },
    {
      key: "ageInMonths",
      dataIndex: "ageInMonths",
      title: _t("age", "months_short", ", "),
      width: 72,
    },
    {
      key: "mileage",
      dataIndex: "mileage",
      title: _t("mileage_unit"),
      width: 112,
      render(value, record, index) {
        if (!record.isAdvertRow) return value;
        const output = record?.regulation?.output
        const input = record?.regulation?.input
        return (
          <Popover title={_t("mileage_regulation")} placement="left"
            content={
              <div>
                <div>
                  <span style={{ display: "inline-block", width: 150 }}>{_t('mileage')}:</span>
                  {int(input?.mileage)}
                </div>
                {input?.overrides?.ageRegulation.active && (
                  <div>
                    <span style={{ display: "inline-block", width: 150 }}>{_t('year_regulation')}:</span>
                    {int(input?.overrides?.ageRegulation.mileage)}
                  </div>
                )}
                <div className="mt-05 text-bold">
                  <span style={{ display: "inline-block", width: 150 }}>{_t('regulated', "mileage_unit")}:</span>
                  {int(output?.ageRegulatedMileage)}
                </div>
              </div>
            }
          >
            <span className={!output ? "text-danger text-bold" : ""} style={{ textDecoration: "underline dotted" }}> {value} </span>
          </Popover>
        )
      },
    },
    {
      key: "horsePower",
      dataIndex: "horsePower",
      title: _t("horse_power"),
      width: 48,
    },
    {
      key: "price",
      dataIndex: "price",
      title: _t("price"),
      width: 80,
      render(value, record, index) {
        if (!record.isAdvertRow) return value;
        const output = record?.regulation?.output
        const input = record?.regulation?.input

        return (
          <Popover title="Salgsprisregulering" placement="left"
            content={
              <div>
                <div>
                  <span style={{ display: "inline-block", width: 150 }}>{_t('sales_price')}:</span>
                  {format.price(input?.salesPrice)}
                </div>
                {input?.overrides?.ageRegulation.active && (
                  <div>
                    <span style={{ display: "inline-block", width: 150 }}>{_t('year_regulation')}:</span>
                    {format.price(input?.overrides?.ageRegulation.price)}
                  </div>
                )}
                {input?.overrides?.plates.active && (
                  <div>
                    <span style={{ display: "inline-block", width: 150 }}>{_t('plade_nr_fee')}:</span>
                    {format.price(input?.overrides?.plates.price)}
                  </div>
                )}
                {input?.overrides?.additionalRegulations.map((reg) => !reg.active ? null : (
                  <div key={reg.regulationReasonId}>
                    <span style={{ display: "inline-block", width: 150 }}>
                      {reg.regulationReason}:
                    </span>
                    {format.price(reg.price)}
                  </div>
                ))}
                <div className="mt-05 text-bold">
                  <span style={{ display: "inline-block", width: 150 }}>{_t('price_level')}:</span>
                  {format.price(output?.tradePriceLevel)}
                </div>
              </div>
            }
          >
            <span className={!output || !output?.tradePriceLevel ? "text-danger text-bold" : ""} style={{ textDecoration: "underline dotted" }}> {value} </span>
          </Popover>
        )
      },
    },
    {
      key: "equipmentPrice",
      dataIndex: "equipmentPrice",
      title: _t("value_of_extra_equipment"),
      align: "right",
      width: 128,
      render(value, record, index) {
        const equipmentTotalNewPrice = equipmnetNewPricesMap[record.id]
        const regulatedEquipmentPrice = equipmentSums[record.id]
        if (!(record.id && isValidNum(equipmentTotalNewPrice) && isValidNum(regulatedEquipmentPrice))) return value;
        const difference = regulatedEquipmentPrice - equipmentTotalNewPrice;
        return (
          <Popover title={_t("extra_equipment_price")} placement="left"
            content={
              <div>
                <div>
                  <span style={{ display: "inline-block", width: 150 }}>{_t('extra_equipments')}:</span>
                  {format.price(equipmentTotalNewPrice)}
                </div>
                <div>
                  <span style={{ display: "inline-block", width: 150 }}>{_t('regulation')}:</span>
                  {format.price(difference)}
                </div>

                <div className="mt-05 text-bold">
                  <span style={{ display: "inline-block", width: 150 }}>{_t('regulated', "price")}:</span>
                  {format.price(regulatedEquipmentPrice)}
                </div>
              </div>
            }
          >
            <span style={{ textDecoration: "underline dotted" }}> {value} </span>
          </Popover>
        )
      },
    },
  ]

  return <Table dataSource={dataSource} columns={columns}
    className="border-light border-bottom-0 advert-management-table"
    size="small"
    pagination={false}
    rowKey={"id"}
    tableLayout="fixed"
    scroll={{ x: 1040 }}
    onHeaderRow={(data) => ({ className: "text-tiny" })}
    onRow={(data) => {
      let className = ""
      if (data.isAdvertRow && (!data.regulation?.output || !data.regulation?.output?.tradePriceLevel)) {
        className = 'highlight-danger'
      }
      if (data.isAveragesRow || data.isDeviationRow) {
        className += "text-bold bg-light"
      }
      return {
        className
      }
    }}
  />
}



const int = (num: any) => format.integer(num)


function TradePriceCalculatorOutput() {
  const { taxFlow, setTaxFlow } = TaxFlowStore.useContainer()

  function setTradePriceInput(val: unknown) {
    const num = safeNum(val)
    const summary = taxFlow.tradePrice?.summary
    setTaxFlow((prev) => ({
      ...prev,
      tradePrice: {
        ...prev.tradePrice,
        tradePrice: prev.autoCalc?.tradePrice ? summary?.finalTradePrice : num ?? undefined,
      }
    }))
  }
  function handleAutoCalcTradePrice(auto: boolean) {
    setTaxFlow((prev) => {
      const calculatedTradePrice = prev.tradePrice?.summary?.finalTradePrice;
      return {
        ...prev,
        tradePrice: {
          ...prev.tradePrice,
          tradePrice: auto ? calculatedTradePrice : prev.tradePrice?.tradePrice,
        },
        autoCalc: {
          ...prev.autoCalc,
          tradePrice: auto
        }
      }
    })
  }

  async function handleMileageOverride() {
    try {
      const effectiveMileageRegulation = taxFlow.tradePrice?.overrides?.mileage ?? taxFlow.tradePrice?.output?.result?.mileageRegulation ?? 0
      const mil = await openModal("overrideMileageRegulation", { mileageRegulation: effectiveMileageRegulation }) as {
        mileageRegulation: number | undefined
      }
      setTaxFlow((prev) => ({
        ...prev,
        tradePrice: {
          ...prev.tradePrice,
          overrides: {
            ...prev.tradePrice?.overrides,
            mileage: safeNum(mil.mileageRegulation) ?? undefined
          }
        }
      }))
    } catch (error) {
      renderErrorMessage(error)
    }
  }

  function setAdditionalRegulationNote(note: string) {
    setTaxFlow((prev) => ({
      ...prev,
      tradePrice: {
        ...prev.tradePrice,
        overrides: {
          ...prev.tradePrice?.overrides,
          additionalRegulation: {
            ...prev.tradePrice?.overrides?.additionalRegulation,
            note: note
          }
        }
      },
    }))
  }

  function setAdditionalRegulation(val: unknown) {
    setTaxFlow((prev) => ({
      ...prev,
      tradePrice: {
        ...prev.tradePrice,
        overrides: {
          ...prev.tradePrice?.overrides,
          additionalRegulation: {
            ...prev.tradePrice?.overrides?.additionalRegulation,
            amount: safeNum(val) ?? undefined
          }
        }
      },
    }))
  }

  const result = taxFlow.tradePrice?.output?.result
  const advertAverages = taxFlow.tradePrice?.output?.advertAverages
  return (
    <div>
      <div className="mt-1 mb-1 border-bottom">
        <OutputItem label={"Gennemsnitlig pris i sammenligningsgrundlag"} value={int(advertAverages?.priceLevelBeforeAdDeduction)} bgLight />
        <OutputItem label={"Annoncefradrag -5%"} value={int(result?.adDeduction)} />
        <OutputItem label={"Handelsprisniveau"} value={int(result?.priceLevel)} bgMuted bold />
      </div>
      <div className="border-bottom">
        <OutputItem label={`Udstyrsregulering (${format.percent(result?.equipmentAgeFactor)})`}
          value={int(result?.equipmentRegulation)} />
        <OutputItem label={`Kilometerstandsregulering (${format.number(result?.mileageFactor)} øre pr km pr 100.000 kr.)`}
          value={
            <Space>
              <Icon name="pencil-simple" size="medium" onClick={handleMileageOverride} />
              <span className="text-strong">{int(taxFlow.tradePrice?.overrides?.mileage ?? result?.mileageRegulation)}</span>
            </Space>

          } bgLight />
        <OutputItem label={`Standsregulering (${format.percent(result?.qualityFactor)})`} value={int(result?.qualityRegulation)} />
        <OutputItem label={`Regulering for særlig anvendelse  (${format.percent(result?.specialUseFactor)})`} value={int(result?.specialUseDeduction)} bgLight />
        {(isValidNum(result?.qualityRegulation) && isValidNum(result.specialUseDeduction)) && (
          <OutputItem label={"Samlet regulering for stand og særlig anvendelse"} value={int(result?.specialUseAndQualitySum)} bold bgLight />
        )}
        <OutputItem label={
          <Input value={taxFlow.tradePrice?.overrides?.additionalRegulation?.note}
            onChange={e => setAdditionalRegulationNote(e.target.value)}
            placeholder="Indtast beskrivelse af ændring"
          />
        } value={
          <InputNum value={taxFlow.tradePrice?.overrides?.additionalRegulation?.amount}
            onChange={setAdditionalRegulation}
            suffix="Kr."
            style={{ maxWidth: 150 }}
          />
        } />
        <OutputItem label={"Beregnet handelspris"} value={
          <CopyableContent contentToCopy={taxFlow?.tradePrice?.summary?.finalTradePrice?.toString() ?? ""}>
            {int(taxFlow?.tradePrice?.summary?.finalTradePrice)}
          </CopyableContent>
        } bgLight bold />
        <OutputItem label={"Anvendt handelspris"} value={
          <Space>
            <InputNum value={taxFlow.tradePrice?.tradePrice}
              suffix="Kr."
              onChange={setTradePriceInput}
              disabled={taxFlow.autoCalc?.tradePrice}
              className="trade-price-input"
              style={{ maxWidth: 150 }}
            />
            <LockSwitch value={taxFlow.autoCalc?.tradePrice} onChange={handleAutoCalcTradePrice} />
          </Space>
        } bold bgMuted />
      </div>
    </div>
  )
}


type OutputItemProps = {
  label: ReactNode;
  value: ReactNode;
  bold?: boolean;
  bgLight?: boolean;
  bgMuted?: boolean;
}
function OutputItem({ label, value, bold, bgLight, bgMuted }: OutputItemProps) {
  const shouldBeCopyable = typeof value === 'string'
  return (
    <Row justify="space-between"
      className={`${bold ? "text-bold" : ""} ${bgLight ? "bg-light" : ""} ${bgMuted ? "bg-muted" : ""} border-light border-bottom-0`}
    >
      <Col className="p-05 d-flex align-items-center">{label}</Col>
      <Col className="p-05 d-flex align-items-center">
        {shouldBeCopyable ? (
          <CopyableContent contentToCopy={value}>
            {value}
          </CopyableContent>
        ) : value}
      </Col>
    </Row>
  )
}



























